import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { astroSettings } from "./astro/crud-settings";
import { IntegrationSettings } from "./define-settings";

export enum CRUDIntegration {
  Astro = "Astro",
}

export const CRUD_INTEGRATION_SETTINGS = {
  [CRUDIntegration.Astro]: astroSettings,
} satisfies Record<CRUDIntegration, IntegrationSettings>;

function variants<
  K extends CRUDIntegration,
  C extends z.ZodType,
  U extends z.ZodType,
  R extends z.ZodType,
>(kind: K, settings: { connect: C; update: U; read: R }) {
  return {
    connect: z.object({ kind: z.literal(kind), settings: settings.connect }),
    update: z.object({ kind: z.literal(kind), settings: settings.update }),
    read: z.object({ kind: z.literal(kind), settings: settings.read }),
  };
}

const astro = variants(CRUDIntegration.Astro, astroSettings);

const connectInput = z.discriminatedUnion("kind", [astro.connect]);
const updateInput = z.discriminatedUnion("kind", [astro.update]);
export const integrationRead = z.discriminatedUnion("kind", [astro.read]);

export const getIntegration = {
  name: "getIntegration" as const,
  input: z.object({ kind: createEnum(CRUDIntegration) }),
  output: z.object({ integration: integrationRead.nullable() }),
};

export const connectIntegration = {
  name: "connectIntegration" as const,
  input: connectInput,
  output: z.object({}),
};

export const updateIntegration = {
  name: "updateIntegration" as const,
  input: updateInput,
  output: z.object({}),
};

export const disconnectIntegration = {
  name: "disconnectIntegration" as const,
  input: z.object({ kind: createEnum(CRUDIntegration) }),
  output: z.object({}),
};

export type ConnectIntegrationInput = z.infer<typeof connectInput>;
export type UpdateIntegrationInput = z.infer<typeof updateInput>;
export type IntegrationRead = z.infer<typeof integrationRead>;

export type ConnectSettings<K extends CRUDIntegration> = Extract<
  ConnectIntegrationInput,
  { kind: K }
>["settings"];

export type UpdateSettings<K extends CRUDIntegration> = Extract<
  UpdateIntegrationInput,
  { kind: K }
>["settings"];

export type ReadSettings<K extends CRUDIntegration> = Extract<
  IntegrationRead,
  { kind: K }
>["settings"];
